// Learner testimonials: "Would you recommend RouteZero?" (Profile) → admin review → landing page.
//
// Two backends with the same API:
//   * Real accounts (Supabase configured): table public.testimonials and the function
//     public.published_testimonials() — supabase/migrations/0002_testimonials.sql. Row Level
//     Security decides who may read or change what; the browser is never trusted to approve.
//   * Development (mock sign-in, no Supabase): rows live in THIS browser only
//     (localStorage key nell:v1:dev:testimonials), so the flow can be tried and tested locally.
//
// Nothing is published unless the learner wrote it, ticked consent, voted 👍 and an admin approved it.

export const MAX_BODY = 600;
export const MAX_NAME = 40;
const KEY = 'testimonials';
const ERROR = 'Could not save right now. Check your connection and try again.';
const clean = (s, n) => String(s ?? '').trim().slice(0, n);
const PUBLIC_COLS = 'id,vote,body,display_name,consent,status,created_at,updated_at';

/** Counts for the admin "recommend" counter. */
export function summarizeTestimonials(rows = []) {
  const yes = rows.filter((r) => r.vote === 1).length;
  const no = rows.filter((r) => r.vote === -1).length;
  const by = (s) => rows.filter((r) => r.status === s).length;
  return {
    total: rows.length, yes, no,
    recommendPct: rows.length ? Math.round((yes / rows.length) * 100) : null,
    withText: rows.filter((r) => r.body).length,
    pending: by('pending'), approved: by('approved'), rejected: by('rejected'),
    published: rows.filter((r) => isPublishable(r) && r.status === 'approved').length,
  };
}

/** Same rule as public.published_testimonials(). */
export const isPublishable = (r) => r.vote === 1 && r.consent === true && !!clean(r.body, MAX_BODY);

function localBackend(devStore, user) {
  const all = () => devStore.get(KEY, {}) || {};
  const save = (rows) => devStore.set(KEY, rows);
  const uid = () => user()?.id || null;
  const write = (patch) => {
    const id = uid(); if (!id) return { ok: false, error: 'Sign in first.' };
    const rows = all(); const now = new Date().toISOString();
    const prev = rows[id] || { id: `dev-${id}`, user_id: id, vote: 1, body: '', display_name: '', consent: false, created_at: now };
    rows[id] = { ...prev, ...patch, status: 'pending', updated_at: now }; // a learner change always needs review again
    save(rows); return { ok: true, row: rows[id] };
  };
  return {
    mode: 'local',
    async mine() { return all()[uid()] || null; },
    async vote(v) { return write({ vote: v }); },
    async submit(p) { return write(p); },
    async published() {
      return Object.values(all()).filter((r) => r.status === 'approved' && isPublishable(r))
        .sort((a, b) => String(b.updated_at).localeCompare(String(a.updated_at))).slice(0, 12)
        .map((r) => ({ display_name: r.display_name || 'RouteZero learner', body: r.body, published_at: r.updated_at }));
    },
    async listAll() { return Object.values(all()).sort((a, b) => String(b.updated_at).localeCompare(String(a.updated_at))); },
    async setStatus(id, status) {
      const rows = all(); const r = Object.values(rows).find((x) => x.id === id);
      if (!r) return { ok: false, error: 'Not found.' };
      rows[r.user_id] = { ...r, status, updated_at: new Date().toISOString() }; save(rows); return { ok: true };
    },
  };
}

function supabaseBackend(client, user) {
  const uid = () => user()?.id || null;
  const table = () => client.from('testimonials');
  const done = ({ error }) => (error ? { ok: false, error: ERROR } : { ok: true });
  return {
    mode: 'accounts',
    async mine() {
      if (!uid()) return null;
      const { data, error } = await table().select(PUBLIC_COLS).eq('user_id', uid()).maybeSingle();
      return error ? null : data;
    },
    async vote(v) {
      if (!uid()) return { ok: false, error: 'Sign in first.' };
      try { return done(await table().upsert({ user_id: uid(), vote: v }, { onConflict: 'user_id' })); } catch { return { ok: false, error: ERROR }; }
    },
    async submit(p) {
      if (!uid()) return { ok: false, error: 'Sign in first.' };
      try { return done(await table().upsert({ user_id: uid(), ...p }, { onConflict: 'user_id' })); } catch { return { ok: false, error: ERROR }; }
    },
    async published() {
      try { const { data, error } = await client.rpc('published_testimonials'); return error || !Array.isArray(data) ? [] : data; } catch { return []; }
    },
    async listAll() {
      const { data, error } = await table().select(PUBLIC_COLS).order('updated_at', { ascending: false });
      if (error) throw new Error(error.message || 'Could not load testimonials');
      return data || [];
    },
    async setStatus(id, status) {
      try { return done(await table().update({ status }).eq('id', id)); } catch { return { ok: false, error: ERROR }; }
    },
  };
}

/**
 * @param {{ client?: object|null, devStore: object, user: () => ({id:string,name:string}|null) }} o
 */
export function createTestimonials({ client = null, devStore, user }) {
  const b = client ? supabaseBackend(client, user) : localBackend(devStore, user);
  return {
    ...b,
    /** Learner: vote first (saved immediately). */
    vote: (v) => (v === 1 || v === -1 ? b.vote(v) : Promise.resolve({ ok: false, error: 'Choose 👍 or 👎.' })),
    /** Learner: the words, the public name and consent (consent only counts with 👍). */
    submit: ({ vote, body, displayName, consent }) => {
      if (vote !== 1 && vote !== -1) return Promise.resolve({ ok: false, error: 'Choose 👍 or 👎 first.' });
      return b.submit({ vote, body: clean(body, MAX_BODY), display_name: clean(displayName, MAX_NAME), consent: vote === 1 && consent === true });
    },
    setStatus: (id, status) => (['pending', 'approved', 'rejected'].includes(status) ? b.setStatus(id, status) : Promise.resolve({ ok: false, error: 'Unknown status.' })),
  };
}
